import * as m from '@staiter/i18n';
import type { ConnectorCapability, SyncSummary, TestConnectionResult } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle2, RefreshCw } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, Checkbox, Input, Select } from '~/components/primitives';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import {
  fetchCatalog,
  fetchConnection,
  fetchDimensions,
  syncConnection,
  testConnection,
  updateConnection,
} from '~/lib/api/integrations-platform';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import styles from './_auth.admin.integrations_.platform.module.css';

export const Route = createFileRoute('/_auth/admin/integrations_/connections/$id')({
  component: ConnectionDetail,
});

type Tab = 'overview' | 'scope' | 'schedule' | 'routing' | 'mapping';

function ConnectionDetail(): JSX.Element {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [syncSummary, setSyncSummary] = useState<SyncSummary | null>(null);

  const conn = useQuery({
    queryKey: ['integrations', 'connection', id],
    queryFn: () => fetchConnection(id),
  });
  const catalog = useQuery({ queryKey: ['integrations', 'catalog'], queryFn: fetchCatalog });
  const descriptor = catalog.data?.items.find((d) => d.key === conn.data?.provider_key);

  const testMut = useMutation({
    mutationFn: () => testConnection(id),
    onSuccess: (r) => {
      setTestResult(r);
      void qc.invalidateQueries({ queryKey: ['integrations', 'connection', id] });
    },
    onError: (e) => toast.push({ variant: 'error', title: renderApiErrorMessage(e) }),
  });
  const syncMut = useMutation({
    mutationFn: () => syncConnection(id),
    onSuccess: (s) => {
      setSyncSummary(s);
      void qc.invalidateQueries({ queryKey: ['integrations', 'connection', id] });
    },
    onError: (e) => toast.push({ variant: 'error', title: renderApiErrorMessage(e) }),
  });

  const c = conn.data;
  if (!c) {
    return (
      <div className={shared.page}>
        <PageHeader title="…" />
      </div>
    );
  }

  const totals = syncSummary
    ? Object.values(syncSummary.by_resource).reduce(
        (a, r) => ({
          imported: a.imported + r.imported,
          updated: a.updated + r.updated,
          skipped: a.skipped + r.skipped,
          unmatched: a.unmatched + r.unmatched,
        }),
        { imported: 0, updated: 0, skipped: 0, unmatched: 0 },
      )
    : null;

  return (
    <div className={shared.page}>
      <PageHeader
        title={c.provider_key}
        description={m.integ_platform_status_label({ status: c.status })}
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={<ArrowLeft size={14} />}
              onClick={() => navigate({ to: '/admin/integrations/platform' })}
            >
              {m.integ_detail_back()}
            </Button>
            {/* Test — первый шаг flow (primary); Sync — после теста (secondary). */}
            <Button
              size="sm"
              leadingIcon={<CheckCircle2 size={14} />}
              onClick={() => testMut.mutate()}
              disabled={testMut.isPending}
            >
              {testMut.isPending ? m.integ_detail_testing() : m.integ_detail_test()}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              leadingIcon={<RefreshCw size={14} />}
              onClick={() => syncMut.mutate()}
              disabled={syncMut.isPending}
            >
              {syncMut.isPending ? m.integ_detail_syncing() : m.integ_detail_sync()}
            </Button>
          </>
        }
      />

      {/* test + sync result */}
      {testResult && (
        <div
          className={[styles.testResult, testResult.health === 'ok' ? styles.testOk : ''].join(' ')}
        >
          {testResult.health === 'ok' ? (
            <>
              <span className={styles.testRow}>
                <CheckCircle2 size={15} aria-hidden="true" /> {m.integ_platform_test_ok()}
              </span>
              {typeof testResult.counterparties_total === 'number' && (
                <span className={styles.testRow}>
                  {m.integ_platform_test_counterparties({ count: testResult.counterparties_total })}
                </span>
              )}
              {typeof testResult.operations_total === 'number' && (
                <span className={styles.testRow}>
                  {m.integ_platform_test_operations({ count: testResult.operations_total })}
                </span>
              )}
              {Array.isArray(testResult.external_entities) && (
                <span className={styles.testRow}>
                  {m.integ_platform_test_entities({ count: testResult.external_entities.length })}
                </span>
              )}
            </>
          ) : (
            <span className={styles.testRow}>
              {testResult.reason_code
                ? m.error_by_code(testResult.reason_code)
                : m.integ_platform_test_failed()}
            </span>
          )}
        </div>
      )}
      {tab === 'overview' && totals && (
        <div className={styles.testResult}>
          <span
            className={[styles.summaryRow, totals.unmatched > 0 ? styles.summaryWarn : ''].join(
              ' ',
            )}
          >
            {m.integ_detail_sync_summary(totals)}
          </span>
        </div>
      )}

      {/* tabs */}
      <div className={styles.tabBar}>
        {(['overview', 'scope', 'schedule', 'routing', 'mapping'] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={[styles.tab, tab === t ? styles.tabActive : ''].join(' ')}
            onClick={() => setTab(t)}
          >
            {t === 'overview' && m.integ_detail_overview()}
            {t === 'scope' && m.integ_detail_tab_scope()}
            {t === 'schedule' && m.integ_detail_tab_schedule()}
            {t === 'routing' && m.integ_detail_tab_routing()}
            {t === 'mapping' && m.integ_detail_tab_mapping()}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className={shared.detailCard}>
          <ChipRow label={m.integ_detail_status()}>
            <span className={[styles.statusChip, styles.chipOk].join(' ')}>
              <CheckCircle2 size={12} aria-hidden="true" />
              {m.integ_platform_status_label({ status: c.status })}
            </span>
          </ChipRow>
          <ChipRow label={m.integ_platform_col_health()}>
            <span
              className={[
                styles.statusChip,
                c.last_pull_at ? styles.chipOk : styles.chipNeutral,
              ].join(' ')}
            >
              {c.last_pull_at
                ? m.integration_health_label({ health: c.health })
                : m.integ_health_not_synced()}
            </span>
          </ChipRow>
          <Row
            label={m.integ_platform_col_schedule()}
            value={m.integ_platform_schedule_label({ schedule: c.schedule })}
          />
          <Row
            label={m.integ_platform_col_last_pull()}
            value={c.last_pull_at ? new Date(c.last_pull_at).toLocaleString() : '—'}
          />
          {/* UX: явный «следующий шаг», пока связь не проверена/синхронизирована. */}
          {!c.last_pull_at && (
            <div className={styles.nextHint}>
              <CheckCircle2 size={14} aria-hidden="true" />
              {m.integ_next_step_test()}
            </div>
          )}
        </div>
      )}

      {tab === 'scope' && descriptor && (
        <ScopeTab id={id} caps={descriptor.capabilities} current={c.scope.resources} />
      )}
      {tab === 'schedule' && <ScheduleTab id={id} current={c.schedule} cron={c.schedule_cron} />}
      {tab === 'routing' && (
        <RoutingTab
          id={id}
          externalEntities={testResult?.external_entities ?? null}
          routingMap={c.routing_map}
        />
      )}
      {tab === 'mapping' && <MappingTab id={id} mappingJson={c.mapping_json} />}
    </div>
  );
}

// ─── F6 — article mapping (passthrough default) ───────────────────────────
function MappingTab({
  id,
  mappingJson,
}: {
  id: string;
  mappingJson: Record<string, string>;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const dims = useQuery({
    queryKey: ['integrations', 'connection', id, 'dimensions'],
    queryFn: () => fetchDimensions(id),
  });
  const [map, setMap] = useState<Record<string, string>>(mappingJson);
  const [search, setSearch] = useState('');
  const [onlyOverridden, setOnlyOverridden] = useState(false);
  const save = useMutation({
    mutationFn: () => updateConnection(id, { mapping_json: map }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.integ_saved() });
      void qc.invalidateQueries({ queryKey: ['integrations', 'connection', id] });
    },
  });
  const articles = [
    ...new Set((dims.data?.items ?? []).map((d) => d.cf_article).filter((a): a is string => !!a)),
  ].sort((a, b) => a.localeCompare(b));
  if (articles.length === 0) {
    return (
      <div className={shared.detailCard}>
        <p className={styles.credsHint}>{m.integ_mapping_empty()}</p>
      </div>
    );
  }
  // passthrough = пустой input (placeholder); override = введённое значение.
  const isOverridden = (a: string): boolean => !!map[a] && map[a] !== a;
  const overriddenCount = articles.filter(isOverridden).length;
  const visible = articles.filter(
    (a) =>
      (!search || a.toLowerCase().includes(search.toLowerCase())) &&
      (!onlyOverridden || isOverridden(a)),
  );
  return (
    <div className={shared.detailCard}>
      <p className={styles.credsHint}>{m.integ_mapping_hint()}</p>
      <div className={styles.mappingHead}>
        <span className={styles.mappingCount}>
          {m.integ_mapping_count({ total: articles.length, overridden: overriddenCount })}
        </span>
        <Input
          label={m.integ_mapping_search()}
          hideLabel
          placeholder={m.integ_mapping_search()}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Checkbox
          label={m.integ_mapping_only_overridden()}
          checked={onlyOverridden}
          onChange={(e) => setOnlyOverridden(e.target.checked)}
        />
      </div>
      <div className={styles.mappingList}>
        {visible.map((article) => {
          const overridden = isOverridden(article);
          return (
            <div
              key={article}
              className={[styles.mappingRow, overridden ? styles.mappingRowOverridden : '']
                .filter(Boolean)
                .join(' ')}
            >
              <span className={styles.mappingSource}>
                {article}
                {overridden && (
                  <span className={styles.mappingOverridden}>
                    {m.integ_mapping_overridden_badge()}
                  </span>
                )}
              </span>
              <span className={styles.mappingArrow}>→</span>
              <Input
                label={article}
                hideLabel
                value={map[article] ?? ''}
                placeholder={article}
                onChange={(e) => setMap((mm) => ({ ...mm, [article]: e.target.value }))}
              />
            </div>
          );
        })}
      </div>
      <div className={styles.saveRow}>
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {m.integ_save()}
        </Button>
      </div>
    </div>
  );
}

function ChipRow({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className={shared.detailRow}>
      <span className={shared.detailRowLabel}>{label}</span>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className={shared.detailRow}>
      <span className={shared.detailRowLabel}>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

// ─── F4 — scope (capability toggles) ──────────────────────────────────────
function ScopeTab({
  id,
  caps,
  current,
}: {
  id: string;
  caps: ConnectorCapability[];
  current: ConnectorCapability[];
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set(current));
  const save = useMutation({
    mutationFn: () =>
      updateConnection(id, { scope: { resources: [...selected] as ConnectorCapability[] } }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.integ_saved() });
      void qc.invalidateQueries({ queryKey: ['integrations', 'connection', id] });
    },
  });
  return (
    <div className={shared.detailCard}>
      <p className={styles.credsHint}>{m.integ_scope_hint()}</p>
      <div className={styles.scopeList}>
        {caps.map((cap) => (
          <Checkbox
            key={cap}
            label={m.integ_platform_capability_label({ capability: cap })}
            checked={selected.has(cap)}
            onChange={(e) =>
              setSelected((s) => {
                const n = new Set(s);
                if (e.target.checked) n.add(cap);
                else n.delete(cap);
                return n;
              })
            }
          />
        ))}
      </div>
      <div className={styles.saveRow}>
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {m.integ_save()}
        </Button>
      </div>
    </div>
  );
}

// ─── F5 — schedule ────────────────────────────────────────────────────────
function ScheduleTab({
  id,
  current,
  cron,
}: {
  id: string;
  current: string;
  cron: string | null;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [preset, setPreset] = useState(current);
  const [cronExpr, setCronExpr] = useState(cron ?? '');
  const save = useMutation({
    mutationFn: () =>
      updateConnection(id, {
        schedule: preset as 'manual' | 'hourly' | 'daily' | 'custom',
        schedule_cron: preset === 'custom' ? cronExpr : null,
      }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.integ_saved() });
      void qc.invalidateQueries({ queryKey: ['integrations', 'connection', id] });
    },
  });
  return (
    <div className={shared.detailCard}>
      <p className={styles.credsHint}>{m.integ_schedule_hint()}</p>
      <Select
        label={m.integ_platform_col_schedule()}
        value={preset}
        onChange={(e) => setPreset(e.target.value)}
        options={[
          { value: 'manual', label: m.integ_platform_schedule_label({ schedule: 'manual' }) },
          { value: 'hourly', label: m.integ_platform_schedule_label({ schedule: 'hourly' }) },
          { value: 'daily', label: m.integ_platform_schedule_label({ schedule: 'daily' }) },
          { value: 'custom', label: m.integ_platform_schedule_label({ schedule: 'custom' }) },
        ]}
      />
      {preset === 'custom' && (
        <div className={styles.formRow}>
          <Input
            label="cron"
            value={cronExpr}
            onChange={(e) => setCronExpr(e.target.value)}
            placeholder="0 6 * * *"
          />
        </div>
      )}
      <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
        {m.integ_save()}
      </Button>
    </div>
  );
}

// ─── F3 — multi-entity routing ────────────────────────────────────────────
function RoutingTab({
  id,
  externalEntities,
  routingMap,
}: {
  id: string;
  externalEntities: Array<{ external_id: string; name: string }> | null;
  routingMap: Record<string, string | null>;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const memberships = useAuthStore((s) => s.user?.memberships) ?? [];
  const [map, setMap] = useState<Record<string, string | null>>(routingMap);
  const save = useMutation({
    mutationFn: () => updateConnection(id, { routing_map: map }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.integ_saved() });
      void qc.invalidateQueries({ queryKey: ['integrations', 'connection', id] });
    },
  });
  if (!externalEntities || externalEntities.length === 0) {
    return (
      <div className={shared.detailCard}>
        <p className={styles.credsHint}>{m.integ_routing_empty()}</p>
      </div>
    );
  }
  return (
    <div className={shared.detailCard}>
      <p className={styles.credsHint}>{m.integ_routing_hint()}</p>
      {externalEntities.map((ext) => (
        <div key={ext.external_id} className={styles.formRow}>
          <Select
            label={ext.name}
            value={map[ext.external_id] ?? ''}
            onChange={(e) => setMap((mm) => ({ ...mm, [ext.external_id]: e.target.value || null }))}
            options={[
              { value: '', label: m.integ_routing_unmapped() },
              ...memberships.map((mem) => ({ value: mem.entity_id, label: mem.entity_name })),
            ]}
          />
        </div>
      ))}
      <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
        {m.integ_save()}
      </Button>
    </div>
  );
}
